import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'
import classNames from 'classnames'
import type { DialogProps } from '../components/Dialog'
import Dialog, { DialogTitle, DialogActions, DialogContent } from '../components/Dialog'
import Button from '../components/Button'
import IconButton from '../components/IconButton'
import Typography from '../components/Typography'
import Alert from '../components/Alert'
import { useTranslation } from '../providers/TranslationProvider'

/**
 * Shared wording for the "you have unsaved changes" confirmation dialog.
 * Spread into an `openDialog(ConfirmationDialog, { ...unsavedChangesConfirmation, onConfirm, onCancel })`
 * call so the prompt reads identically wherever a dirty form guards navigation.
 */
export const unsavedChangesConfirmation = {
  title: 'Are you sure?',
  message: 'You have unsaved changes. If you leave now, your changes will be lost.',
  yesLabel: "Yes, I'm Sure",
  noLabel: 'Back'
} as const

export interface ConfirmationDialogProps extends DialogProps {
  title?: React.ReactNode
  message?: React.ReactNode
  yesLabel?: React.ReactNode
  noLabel?: React.ReactNode
  scary?: boolean
  warning?: boolean
  severity?: 'error' | 'warning' | 'info' | 'success'
  onConfirm?: (event: React.MouseEvent<HTMLElement>) => void
  onCancel?: (event: React.MouseEvent<HTMLElement>) => void
  onNoLabelClick?: (event: React.MouseEvent<HTMLElement>) => void
  closeButton?: boolean
  hideBackButton?: boolean
  alignLeftButtonWithTitle?: boolean
  /**
   * Opt-in: stretch and center the (non-alert) message instead of the default left-aligned,
   * shrink-to-fit layout. Used for custom centered message content (e.g. the Payrix
   * "Connecting to PaintScout Payments" wait dialog). Leave unset for normal text messages.
   */
  centerMessage?: boolean
}

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  alert: {
    width: '100%'
  },
  // The message wrapper is a flex item inside `content` (justifyContent: flex-start), so it
  // shrinks to fit and pins left by default. width:100% is load-bearing: it stretches the
  // wrapper to the dialog width so the message's own centering (align/alignItems) can take effect.
  centeredMessage: {
    width: '100%'
  },
  scaryButton: {
    background: theme.palette.error.main
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}))
function ConfirmationDialog({
  onCancel,
  onConfirm,
  title,
  message,
  yesLabel,
  noLabel,
  scary,
  warning,
  severity = 'error',
  closeButton,
  onNoLabelClick,
  hideBackButton,
  alignLeftButtonWithTitle,
  centerMessage,
  onClose,
  ...otherProps
}: ConfirmationDialogProps) {
  const [loading, setLoading] = useState(false)
  const classes = useStyles(otherProps)
  const { __ } = useTranslation()
  const showAlert = !!scary || !!warning
  // Defaults must resolve here, not in defaultProps (module scope, no hook access), so callers
  // that omit title/yesLabel/noLabel — e.g. TaskDialog's unsaved-changes prompt — still get a
  // translated prompt instead of a hardcoded English fallback.
  const resolvedTitle = title ?? __('Are you sure?')
  const resolvedYesLabel = yesLabel ?? __("Yes, I'm Sure")
  const resolvedNoLabel = noLabel ?? __('Back')
  async function handleConfirm(ev: any) {
    // set loading is the onConfirm is an async function
    const loadingTimeout = setTimeout(() => setLoading(true), 1)
    try {
      await onConfirm(ev)
    } finally {
      // always reset, even if onConfirm throws — otherwise the 1ms timeout flips loading on
      // and the dialog spins forever with a disabled button (see #21139)
      setLoading(false)
      // cancel the timeout if the async finished quickly / wasn't async
      clearTimeout(loadingTimeout)
    }
  }

  const closeHandler =
    !onCancel && !onClose
      ? undefined
      : (event?: React.MouseEvent<HTMLElement>, reason?: 'backdropClick' | 'escapeKeyDown') => {
          if (onCancel) {
            onCancel(event)
          }
          if (onClose) {
            onClose(event, reason)
          }
        }

  const rightContent =
    closeButton && closeHandler ? (
      <IconButton size="small" onClick={closeHandler} disabled={loading}>
        <ClearIcon />
      </IconButton>
    ) : undefined

  const cancelButtonHandler = onNoLabelClick || closeHandler

  return (
    <Dialog onClose={closeHandler} {...otherProps}>
      {resolvedTitle && (
        <DialogTitle
          loading={loading}
          classes={closeButton ? { root: classes.dialogTitle } : undefined}
          rightContent={rightContent}
        >
          {resolvedTitle}
        </DialogTitle>
      )}
      <DialogContent className={classes.content}>
        {showAlert ? (
          <Alert className={classes.alert} content={message} severity={severity} variant="default" centerIcon={false} />
        ) : (
          <Typography
            className={centerMessage ? classes.centeredMessage : undefined}
            variant="body1"
            component="div"
            align={centerMessage ? 'center' : 'left'}
          >
            {message}
          </Typography>
        )}
      </DialogContent>
      {(!!onConfirm || !!cancelButtonHandler) && (
        <DialogActions
          alignLeftButtonWithTitle={alignLeftButtonWithTitle}
          leftButton={
            !hideBackButton && cancelButtonHandler ? (
              <Button disabled={loading} onClick={cancelButtonHandler} variant={'text'}>
                {resolvedNoLabel}
              </Button>
            ) : null
          }
        >
          <Button
            disabled={loading}
            onClick={handleConfirm}
            variant={'contained'}
            className={classNames({
              [classes.scaryButton]: !!scary,
              'tour-confirmation-dialog-button': true
            })}
          >
            {resolvedYesLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

ConfirmationDialog.defaultProps = {
  closeButton: true
}
export default ConfirmationDialog
